import React, { useState, useMemo, useCallback, useEffect } from "react";
import styled from "styled-components";
import { generateChordGrid, getScaleNotes } from "../utils/chordSuggestions";
import { ALL_ROOTS, ALL_EXTENSIONS, noteNameToMidi } from "../utils/chordUtils";
import { romanToChordSymbol } from "../utils/romanToChordSymbol";

const CHORD_SELECT_GREEN = "#23D18E";
const ROOT_TO_MODAL_ROOT: Record<string, string> = {
  "C#": "Db",
  "D#": "Eb",
  "F#": "Gb",
  "G#": "Ab",
  "A#": "Bb",
  Cb: "B",
  Fb: "E",
  "E#": "F",
  "B#": "C",
};

type ModalRoot = (typeof ALL_ROOTS)[number];
type ModalExtension = (typeof ALL_EXTENSIONS)[number];

interface ChordChangerProps {
  isOpen: boolean;
  onClose: () => void;
  currentChord: string; // Roman numeral like "i", "V", etc.
  currentKey: string;
  mode: "minor" | "major";
  onChordChange: (newChord: string) => void; // Returns new roman numeral
  onPreviewChord?: (roman: string) => void;
}

const ChordChanger: React.FC<ChordChangerProps> = ({
  isOpen,
  onClose,
  currentChord,
  currentKey,
  mode,
  onChordChange,
  onPreviewChord,
}) => {
  const resolveExtensionFromSuffix = (
    rawSuffix: string
  ): ModalExtension => {
    const suffix = rawSuffix.trim();

    if (suffix === "") {
      return "maj";
    }
    if (suffix === "7") return "7";
    if (suffix === "9") return "9";
    if (suffix === "11" || suffix === "13") return "9";
    if (suffix === "m") return "min";
    if (suffix === "dim" || suffix === "dim7") return "dim";
    if (suffix === "aug") return "aug";
    if (suffix === "sus2") return "sus2";
    if (suffix === "sus4") return "sus4";
    if (suffix === "5") return "5";
    if (suffix === "add6") return "add6";
    if (suffix === "add9") return "add9";
    if (suffix === "6") return "6";
    if (suffix === "maj7" || suffix === "maj9" || suffix === "maj11") {
      return "maj7";
    }
    if (suffix === "m6") return "min6";
    if (suffix === "m7" || suffix === "mmaj7") return "min7";
    if (suffix === "m9") return "min9";
    if (/^7[#♯b♭]?9$/.test(suffix)) return "9";
    if (/^(?:maj)?7[#♯b♭]?9$/.test(suffix)) return "9";

    if (suffix.includes("add9")) return "add9";
    if (suffix.includes("add6")) return "add6";
    if (suffix.includes("sus2")) return "sus2";
    if (suffix.includes("sus4")) return "sus4";
    if (suffix.includes("dim")) return "dim";
    if (suffix.includes("aug")) return "aug";
    if (suffix.startsWith("maj")) return "maj";
    if (suffix.startsWith("m")) return "min";

    return "maj";
  };

  const getSelectionFromCurrentChord = useCallback((): {
    root: ModalRoot;
    extension: ModalExtension;
  } => {
    try {
      const chordSymbol = romanToChordSymbol(currentChord, currentKey, mode);
      const rootMatch = chordSymbol.match(/^([A-G](?:#|b)?)(.*)$/);
      if (!rootMatch) {
        throw new Error("Could not parse chord symbol root");
      }

      const [, rawRoot, rawSuffix] = rootMatch;
      const normalizedRoot = ROOT_TO_MODAL_ROOT[rawRoot] ?? rawRoot;
      const root: ModalRoot = (
        ALL_ROOTS.includes(normalizedRoot as ModalRoot)
          ? normalizedRoot
          : "C"
      ) as ModalRoot;

      const extension = resolveExtensionFromSuffix(rawSuffix);
      return { root, extension };
    } catch (_error) {
      const fallbackRoot = ROOT_TO_MODAL_ROOT[currentKey] ?? currentKey;
      const root: ModalRoot = (
        ALL_ROOTS.includes(fallbackRoot as ModalRoot)
          ? fallbackRoot
          : "C"
      ) as ModalRoot;
      return { root, extension: "maj" };
    }
  }, [currentChord, currentKey, mode]);

  // Convert mode to chordSuggestions format
  const suggestionMode: "major" | "naturalMinor" | "harmonicMinor" =
    mode === "major" ? "major" : "naturalMinor";

  // Generate chord grid for color coding
  const chordGrid = useMemo(
    () => generateChordGrid(currentKey, suggestionMode),
    [currentKey, suggestionMode]
  );

  // State for selected root and extension
  const [selectedRoot, setSelectedRoot] = useState<string>("C");
  const [selectedExtension, setSelectedExtension] =
    useState<ModalExtension>("maj");

  useEffect(() => {
    if (!isOpen) return;
    const { root, extension } = getSelectionFromCurrentChord();
    setSelectedRoot(root);
    setSelectedExtension(extension);
  }, [getSelectionFromCurrentChord, isOpen]);

  // Handle root selection
  const handleRootClick = (root: string) => {
    setSelectedRoot(root);
  };

  // Handle extension selection (single selection)
  const handleExtensionClick = (extension: ModalExtension) => {
    setSelectedExtension(extension);
    const previewRoman = convertToRomanNumeral(
      selectedRoot,
      extension,
      currentKey,
      mode
    );
    onPreviewChord?.(previewRoman);
  };

  // Convert root + extension to roman numeral
  const convertToRomanNumeral = (
    root: string,
    extension: ModalExtension,
    currentKey: string,
    mode: "minor" | "major"
  ): string => {
    try {
      // Get scale notes for the current key
      // Use harmonic minor here to match romanParser/romanToChordSymbol semantics.
      // Using natural minor causes bVII roots (e.g. Bb in C minor) to be encoded as VII.
      const romanMappingMode: "major" | "naturalMinor" | "harmonicMinor" =
        mode === "major" ? "major" : "harmonicMinor";
      const scaleNotes = getScaleNotes(currentKey, romanMappingMode);

      // Get the root MIDI note
      const rootMidi = noteNameToMidi(root, 4);
      const rootPitchClass = rootMidi % 12;

      // Find which scale degree this root corresponds to
      const scalePitchClasses = scaleNotes.map((note) => note % 12);
      let degreeIndex = scalePitchClasses.indexOf(rootPitchClass);
      let accidentalSymbol = "";

      if (degreeIndex === -1) {
        const chromaticMatch = scalePitchClasses
          .map((pitchClass, index) => {
            const diff = (rootPitchClass - pitchClass + 12) % 12;
            if (diff === 1) {
              return { index, accidentalSymbol: "♯" };
            }
            if (diff === 11) {
              return { index, accidentalSymbol: "♭" };
            }
            return null;
          })
          .find(Boolean);

        if (!chromaticMatch) {
          return currentChord;
        }

        degreeIndex = chromaticMatch.index;
        accidentalSymbol = chromaticMatch.accidentalSymbol;
      }

      // Convert degree index (0-based) to roman numeral (1-based)
      const romanNumerals = ["I", "II", "III", "IV", "V", "VI", "VII"];

      // Determine if it should be uppercase (major) or lowercase (minor)
      // Based on the extension
      const isMajor =
        extension === "maj" ||
        extension === "maj7" ||
        extension === "7" ||
        extension === "9" ||
        extension === "aug" ||
        extension === "sus2" ||
        extension === "sus4" ||
        extension === "6" ||
        extension === "add6" ||
        extension === "add9" ||
        extension === "5";
      const isDim = extension === "dim";
      const isMin =
        extension === "min" ||
        extension === "min7" ||
        extension === "min6" ||
        extension === "min9";

      let roman = isMajor
        ? romanNumerals[degreeIndex]
        : isMin
        ? romanNumerals[degreeIndex].toLowerCase()
        : romanNumerals[degreeIndex].toLowerCase();

      if (accidentalSymbol) {
        roman = `${accidentalSymbol}${roman}`;
      }

      // Add quality suffixes
      if (isDim) {
        roman += "°";
      } else if (extension === "aug") {
        roman += "+";
      } else if (extension === "5") {
        roman += "5";
      } else if (extension === "sus2") {
        roman = roman.toLowerCase() + "sus2";
      } else if (extension === "sus4") {
        roman = roman.toLowerCase() + "sus4";
      }

      // Add extensions
      if (extension === "maj7") {
        roman += "maj7";
      } else if (extension === "9") {
        roman += "9";
      } else if (extension === "min7") {
        roman += "7";
      } else if (extension === "7") {
        roman += "7";
      } else if (extension === "min6") {
        roman += "6";
      } else if (extension === "6") {
        roman += "6";
      } else if (extension === "min9") {
        roman += "9";
      } else if (extension === "add6") {
        roman += "add6";
      } else if (extension === "add9") {
        roman += "add9";
      }

      return roman;
    } catch (error) {
      console.error("Error converting to roman numeral:", error);
      return currentChord; // Fallback to current chord
    }
  };

  // Handle apply button (convert and call onChordChange)
  const handleApply = () => {
    const newRoman = convertToRomanNumeral(
      selectedRoot,
      selectedExtension,
      currentKey,
      mode
    );
    onChordChange(newRoman);
    onClose();
  };

  // Handle randomise
  const handleRandomise = () => {
    // Randomly select a root
    const randomRoot = ALL_ROOTS[Math.floor(Math.random() * ALL_ROOTS.length)];
    setSelectedRoot(randomRoot);

    // Randomly select an extension
    const randomExtension =
      ALL_EXTENSIONS[Math.floor(Math.random() * ALL_EXTENSIONS.length)];
    setSelectedExtension(randomExtension);

    const previewRoman = convertToRomanNumeral(
      randomRoot,
      randomExtension,
      currentKey,
      mode
    );
    onPreviewChord?.(previewRoman);
  };

  const extensionRows = useMemo(() => {
    const midpoint = Math.ceil(ALL_EXTENSIONS.length / 2);
    return [ALL_EXTENSIONS.slice(0, midpoint), ALL_EXTENSIONS.slice(midpoint)];
  }, []);

  // Get color for root button
  const getRootColor = (root: string): "green" | "yellow" => {
    const suggestion = chordGrid[root]?.["maj"]; // Use maj as default for root color
    return suggestion?.rootColor || "yellow";
  };

  // Check if root matches the current key
  const isRootInKey = useCallback(
    (root: string): boolean => {
      // Direct match (case-sensitive)
      if (currentKey === root) return true;

      // Handle enharmonic equivalents
      const enharmonicMap: Record<string, string[]> = {
        C: ["C", "B#"],
        "C#": ["C#", "Db"],
        Db: ["Db", "C#"],
        D: ["D"],
        "D#": ["D#", "Eb"],
        Eb: ["Eb", "D#"],
        E: ["E", "Fb"],
        F: ["F", "E#"],
        "F#": ["F#", "Gb"],
        Gb: ["Gb", "F#"],
        G: ["G"],
        "G#": ["G#", "Ab"],
        Ab: ["Ab", "G#"],
        A: ["A"],
        "A#": ["A#", "Bb"],
        Bb: ["Bb", "A#"],
        B: ["B", "Cb"],
      };

      const keyEquivalents = enharmonicMap[currentKey] || [currentKey];
      const rootEquivalents = enharmonicMap[root] || [root];

      // Check if any equivalent matches
      return keyEquivalents.some((k) => rootEquivalents.includes(k));
    },
    [currentKey]
  );

  // Get color for extension button
  const getExtensionColor = (
    root: string,
    extension: ModalExtension
  ): "green" | "yellow" | "red" => {
    const suggestion = chordGrid[root]?.[extension];
    return suggestion?.extensionColor || "red";
  };

  if (!isOpen) return null;

  return (
    <>
      <Overlay onClick={onClose} id="chord-changer-overlay" />
      <ModalContainer id="chord-changer-modal">
        {/* Root Note Selection Section */}
        <Section id="root-section">
          <ScrollContainer id="root-scroll-container">
            <RootButtonsContainer id="root-buttons-container">
              {ALL_ROOTS.map((root) => {
                const isSelected = selectedRoot === root;
                const rootColor = getRootColor(root);
                const isKeyRoot = isRootInKey(root);
                return (
                  <RootButton
                    key={root}
                    id={`root-button-${root}`}
                    $isSelected={isSelected}
                    $color={rootColor}
                    $isKeyRoot={isKeyRoot}
                    onClick={() => handleRootClick(root)}
                  >
                    {root}
                  </RootButton>
                );
              })}
            </RootButtonsContainer>
          </ScrollContainer>
        </Section>

        {/* Chord Extension Selection Section */}
        <Section id="extension-section">
          <ScrollContainer id="extension-scroll-container">
            <ExtensionButtonsContainer id="extension-buttons-container">
              {/* First row of extensions */}
              <ExtensionRow id="extension-row-1">
                {extensionRows[0].map((extension) => {
                  const isSelected = selectedExtension === extension;
                  const extensionColor = getExtensionColor(
                    selectedRoot,
                    extension
                  );
                  return (
                    <ExtensionButton
                      key={extension}
                      id={`extension-button-${extension}`}
                      $isSelected={isSelected}
                      $color={extensionColor}
                      onClick={() => handleExtensionClick(extension)}
                    >
                      {extension}
                    </ExtensionButton>
                  );
                })}
              </ExtensionRow>
              {/* Second row of extensions */}
              <ExtensionRow id="extension-row-2">
                {extensionRows[1].map((extension) => {
                  const isSelected = selectedExtension === extension;
                  const extensionColor = getExtensionColor(
                    selectedRoot,
                    extension
                  );
                  return (
                    <ExtensionButton
                      key={extension}
                      id={`extension-button-${extension}`}
                      $isSelected={isSelected}
                      $color={extensionColor}
                      onClick={() => handleExtensionClick(extension)}
                    >
                      {extension}
                    </ExtensionButton>
                  );
                })}
              </ExtensionRow>
            </ExtensionButtonsContainer>
          </ScrollContainer>
        </Section>

        {/* Randomise Button */}
        <RandomiseButton id="randomise-button" onClick={handleRandomise}>
          Randomise
        </RandomiseButton>

        {/* Apply Button */}
        <ApplyButton id="apply-button" onClick={handleApply}>
          Apply
        </ApplyButton>
      </ModalContainer>
    </>
  );
};

export default ChordChanger;

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1998;
  animation: fadeIn 0.3s ease-out forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #2a2a2a;
  border-radius: 8px;
  padding: 16px;
  z-index: 1999;
  width: 760px;
  max-width: 90vw;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 2px solid #b0b0b0;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid #fff;
  padding-top: 12px;
  padding-bottom: 12px;

  &:first-child {
    border-top: none;
    padding-top: 0;
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
`;

const RootButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow: visible;
  flex: 1;
`;

const RootButton = styled.button<{
  $isSelected: boolean;
  $color: "green" | "yellow";
  $isKeyRoot: boolean;
}>`
  background: ${(props) => {
    // Priority: if selected and is key root, always green
    if (props.$isSelected && props.$isKeyRoot) {
      return CHORD_SELECT_GREEN; // Green when selected and is key root
    }
    // If selected but not key root, use yellow
    if (props.$isSelected) {
      return props.$color === "green" ? CHORD_SELECT_GREEN : "#ffc107";
    }
    // Unselected: dark background
    return "#2a2a2a";
  }};
  color: ${(props) => {
    if (props.$isSelected) {
      return "#000"; // Black text when selected (green or yellow background)
    }
    return "#fff"; // White text when unselected
  }};
  border: 1px solid
    ${(props) => {
      // Priority: if selected and is key root, always green border
      if (props.$isSelected && props.$isKeyRoot) {
        return CHORD_SELECT_GREEN; // Green border when selected and is key root
      }
      // If selected but not key root, yellow border
      if (props.$isSelected) {
        return props.$color === "green" ? CHORD_SELECT_GREEN : "#ffc107";
      }
      // Unselected: use color from suggestion (green or yellow)
      return props.$color === "green" ? CHORD_SELECT_GREEN : "#ffc107";
    }};
  border-radius: 4px;
  padding: 8px 16px;
  font-family: "Dogica Pixel", monospace;
  font-size: 10px;
  font-weight: ${(props) => (props.$isSelected ? 700 : 400)};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  -webkit-font-smoothing: none;
  -moz-osx-font-smoothing: unset;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;

  &:hover {
    background: ${(props) => {
      if (props.$isSelected && props.$isKeyRoot) {
        return CHORD_SELECT_GREEN; // Lighter green on hover
      }
      if (props.$isSelected) {
        return props.$color === "green" ? CHORD_SELECT_GREEN : "#ffca28";
      }
      return "#3a3a3a"; // Darker on hover when unselected
    }};
    border-color: ${(props) => {
      if (props.$isSelected && props.$isKeyRoot) {
        return CHORD_SELECT_GREEN; // Lighter green border on hover
      }
      if (props.$isSelected) {
        return props.$color === "green" ? CHORD_SELECT_GREEN : "#ffca28";
      }
      return props.$color === "green" ? CHORD_SELECT_GREEN : "#ffca28"; // Lighter border on hover
    }};
  }
`;

const ExtensionButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: visible;
  flex: 1;
`;

const ExtensionRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
`;

const ExtensionButton = styled.button<{
  $isSelected: boolean;
  $color: "green" | "yellow" | "red";
}>`
  background: ${(props) => {
    if (props.$isSelected) {
      return props.$color === "green"
        ? CHORD_SELECT_GREEN
        : props.$color === "yellow"
        ? "#ffc107"
        : "#f44336";
    }
    return "#2a2a2a";
  }};
  color: ${(props) => (props.$isSelected ? "#000" : "#fff")};
  border: 1px solid
    ${(props) => {
      if (props.$isSelected) {
        return props.$color === "green"
          ? CHORD_SELECT_GREEN
          : props.$color === "yellow"
          ? "#ffc107"
          : "#f44336";
      }
      return props.$color === "green"
        ? CHORD_SELECT_GREEN
        : props.$color === "yellow"
        ? "#ffc107"
        : "#f44336";
    }};
  border-radius: 4px;
  padding: 8px 16px;
  font-family: "Dogica Pixel", monospace;
  font-size: 10px;
  font-weight: ${(props) => (props.$isSelected ? 700 : 400)};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  -webkit-font-smoothing: none;
  -moz-osx-font-smoothing: unset;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;

  &:hover {
    opacity: 0.9;
  }
`;

const RandomiseButton = styled.button`
  background: #2a2a2a;
  border: 1px solid #fff;
  border-radius: 4px;
  padding: 12px 24px;
  color: #fff;
  font-family: "Dogica Pixel", monospace;
  font-size: 10px;
  font-weight: 400;
  cursor: pointer;
  width: 100%;
  margin-top: 12px;
  transition: all 0.2s ease;
  -webkit-font-smoothing: none;
  -moz-osx-font-smoothing: unset;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;

  &:hover {
    background: #3a3a3a;
    color: #fff;
  }
`;

const ApplyButton = styled.button`
  background: ${CHORD_SELECT_GREEN};
  border: 1px solid ${CHORD_SELECT_GREEN};
  border-radius: 4px;
  padding: 12px 24px;
  color: #000;
  font-family: "Dogica Pixel", monospace;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-top: 8px;
  transition: all 0.2s ease;
  -webkit-font-smoothing: none;
  -moz-osx-font-smoothing: unset;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;

  &:hover {
    background: ${CHORD_SELECT_GREEN};
    border-color: ${CHORD_SELECT_GREEN};
  }
`;
